import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { FunnelIcon, PlusIcon } from '@heroicons/react/24/outline'
import { useAuth } from '../../contexts/AuthContext'
import FlexyTrasladosTable from '../../components/integracion-flexy/traslados/FlexyTrasladosTable'
import FlexyTrasladoDetailModal from '../../components/integracion-flexy/traslados/FlexyTrasladoDetailModal'
import { useTraslados, useTrasladoActions } from '../../components/integracion-flexy/hooks/useTraslados'
import Button from '../../components/ui/Button/Button'
import { FilterPanel } from '../../components/ui'
import type { FilterSection } from '../../components/ui/FilterPanel'
import PaginationControls from '../../components/ui/Pagination/PaginationControls'
import type { Traslado } from '../../types/traslados'

const ESTADO_OPTIONS = [
  { value: 'pendiente', label: 'Pendiente' },
  { value: 'aprobado', label: 'Aprobado' },
  { value: 'anulado', label: 'Anulado' },
  { value: 'error', label: 'Error' },
  { value: 'error_flexy', label: 'Error Flexy' },
  { value: 'vencido', label: 'Vencido' }
]

type EstadoOption = { label: string; value: string }

const FlexyTrasladosListPage: React.FC = () => {
  const { hasPermission } = useAuth()
  const canManage = hasPermission('manage_wms_traslados')
  const {
    traslados,
    estados,
    setEstados,
    page,
    setPage,
    totalPages,
    isLoading,
    refetch
  } = useTraslados()

  const { reenviarFlexy, retryingId } = useTrasladoActions(refetch)

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [showFilters, setShowFilters] = useState(false)
  const [filterByState, setFilterByState] = useState(false)
  const [selectedStates, setSelectedStates] = useState<EstadoOption[]>([])

  const handleView = (traslado: Traslado) => {
    setSelectedId(traslado.id)
    setDetailOpen(true)
  }

  const handleRetry = (traslado: Traslado) => {
    reenviarFlexy(traslado.id, false)
  }

  const applyEstadosFilter = (nextFilterByState: boolean, nextSelected: EstadoOption[]) => {
    const nextEstados = nextFilterByState
      ? nextSelected.map((option) => option.value)
      : []
    const changed =
      nextEstados.length !== estados.length ||
      nextEstados.some((value, index) => value !== estados[index])

    setEstados(nextEstados)
    if (changed) setPage(1)
  }

  const handleFilterByState = (checked: boolean) => {
    setFilterByState(checked)
    if (!checked) {
      setSelectedStates([])
      applyEstadosFilter(false, [])
      return
    }
    applyEstadosFilter(true, selectedStates)
  }

  const handleSelectedStatesChange = (nextSelected: EstadoOption[]) => {
    setSelectedStates(nextSelected)
    applyEstadosFilter(filterByState, nextSelected)
  }

  const handleClearAllFilters = () => {
    setFilterByState(false)
    setSelectedStates([])
    applyEstadosFilter(false, [])
  }

  const activeFiltersCount = filterByState && selectedStates.length > 0 ? 1 : 0

  const filterSections: FilterSection[] = [
    {
      id: 'quick',
      title: 'Filtros Rápidos',
      filters: [
        {
          id: 'state',
          label: 'Filtrar por estado',
          type: 'switch',
          checked: filterByState,
          onToggle: handleFilterByState,
          selectProps: filterByState
            ? {
                isMulti: true,
                options: ESTADO_OPTIONS,
                value: selectedStates,
                onChange: (newValue) =>
                  handleSelectedStatesChange((newValue as EstadoOption[]) || []),
                placeholder: 'Seleccionar estados...'
              }
            : undefined
        }
      ]
    }
  ]

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto w-full max-w-full px-3 py-8 sm:px-4 lg:px-6">
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl font-bold">Transferencias Flexy</h1>
            <p className="mt-1 text-sm text-gray-500">
              Gestiona transferencias WMS entre almacenes
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="relative overflow-visible">
              <Button
                onClick={(event) => {
                  event.stopPropagation()
                  setShowFilters(!showFilters)
                }}
                onMouseDown={(event) => {
                  event.stopPropagation()
                }}
                variant="secondary"
                leftIcon={FunnelIcon}
                className="relative"
              >
                Filtros
                {activeFiltersCount > 0 && (
                  <span className="absolute -top-1 -right-1 flex min-w-[20px] items-center justify-center rounded-full bg-blue-600 px-1.5 py-0.5 text-xs font-bold text-white">
                    {activeFiltersCount}
                  </span>
                )}
              </Button>
              <FilterPanel
                isOpen={showFilters}
                onClose={() => setShowFilters(false)}
                sections={filterSections}
                onClearAll={handleClearAllFilters}
                activeFiltersCount={activeFiltersCount}
                position="right"
              />
            </div>
            {canManage && (
              <Link to="/flexy/traslados/new">
                <Button leftIcon={PlusIcon}>Crear transferencia</Button>
              </Link>
            )}
          </div>
        </div>

        <FlexyTrasladosTable
          traslados={traslados}
          isLoading={isLoading}
          onView={handleView}
          onRetry={canManage ? handleRetry : undefined}
          retryingId={retryingId}
          canManage={canManage}
        />

        {totalPages > 1 && (
          <div className="mt-4 flex justify-center">
            <PaginationControls
              currentPage={page}
              totalPages={totalPages}
              onPageChange={setPage}
              isLoading={isLoading}
            />
          </div>
        )}
      </div>

      <FlexyTrasladoDetailModal
        trasladoId={selectedId}
        isOpen={detailOpen}
        onClose={() => setDetailOpen(false)}
        onUpdated={refetch}
      />
    </div>
  )
}

export default FlexyTrasladosListPage
